import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";

import { useJsonApi } from "@/modules/api/hooks/useJsonApi";
import { apiErrorValidate } from "@/modules/api/utils/apiErrorValidate";
import { useAuthContext } from "@/modules/auth/hooks/useAuthContext";
import { getUnacknowledgedLevelsQuery } from "@/modules/levels/api/queries/getUnacknowledgedLevelsQuery";

import { parseUnacknowledgedLevels } from "./parseUnacknowledgedLevels";

const translationNamespace = "apiMessages/useGetUnacknowledgedLevels";

export type ParsedUnacknowledgedLevel = {
  id: string;
  levelName: string;
  order: number;
  group:
    | {
        id: string;
        name: string;
        colorHex: string;
      }
    | undefined;
};

export function useGetUnacknowledgedLevels() {
  const jsonApi = useJsonApi();
  const { t } = useTranslation(translationNamespace);

  const { userCompanyId } = useAuthContext();
  return useQuery(
    userCompanyId
      ? {
          queryKey: ["getUnacknowledgedLevels", userCompanyId],
          queryFn: () =>
            getUnacknowledgedLevelsQuery(jsonApi, {
              companyId: userCompanyId,
            }),
          retry: false,
          refetchOnWindowFocus: false,
          select: (response) => {
            if (response) {
              return parseUnacknowledgedLevels(response);
            }
          },
          onError: (error) => {
            apiErrorValidate(error, {
              401: {
                UNAUTHORIZED: t(
                  "You are not allowed to view unacknowledged levels. Please sign in again"
                ),
              },
              403: {
                ACCESS_FORBIDDEN: t(
                  "You don't have an access to view unacknowledged tasks"
                ),
              },
              404: {
                USER_NOT_FOUND: t("User cannot be found"),
              },
            });
          },
        }
      : { enabled: false }
  );
}
